import type { GameId } from '../curriculum'

/**
 * Teacher Play Mode's one piece of state — *which game did the teacher pick?*
 *
 * 🔴 IT EXISTS BECAUSE D3 ASKS A QUESTION THE CHILD'S FLOW ALREADY ASKS. The
 * teacher confirm screen picks a child **and a game**; `KidApp` starts at
 * `home`, where the child picks a game. Dropping the teacher's choice would make
 * the picker on D3 a control that does nothing, and a four-year-old handed a
 * tablet mid-lesson should not have to find the tile their teacher just tapped.
 *
 * 🔴 A MODULE WITH A STORAGE BACKING, NOT A PROP, AND THE HASH IS WHY. The
 * teacher tier and the child's app are two branches of `AppRouter`; the handoff
 * crosses a `window.location.hash` write, and a prop cannot. `localStorage`
 * rather than module state because a school laptop's "open in a new tab" is a
 * real thing a teacher does, and the alternative — a game choice that silently
 * evaporates — looks exactly like the disabled button this feature replaced.
 *
 * 🔴 IT IS KEYED BY `playerId` AND CONSUMED EXACTLY ONCE. Both halves matter on
 * a shared classroom laptop:
 *
 *   · **Keyed**, so a handoff written for Kabir cannot open a game for the next
 *     child who signs in. `kidSession.ts` already had to learn this lesson the
 *     hard way — the wrong name simply being already there is the worst failure
 *     shape this tier has.
 *   · **Once**, so a child who finishes the round and taps Home is not dragged
 *     straight back into the same game for the rest of the morning.
 *
 * ⚠️ IT CARRIES NO CREDENTIAL AND NO CHILD DATA. A `playerId` and a game id,
 * both opaque; the session itself lives in `kidSession.ts`, which is the only
 * thing holding a token.
 */

const HANDOFF_KEY = 'kalqy.playHandoff'

export interface PlayHandoff {
  /** Whose launch this was. Checked before the game is opened. */
  playerId: string
  gameId: GameId
}

/**
 * Minimal `localStorage` shape, injectable so the tests (and Node) do not need
 * a DOM, and so a browser that throws on access cannot take a launch down.
 * The same shim `kidSession.ts` and `device.ts` carry, deliberately identical.
 */
export interface HandoffStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

function defaultStorage(): HandoffStorage | null {
  try {
    return (globalThis as { localStorage?: HandoffStorage }).localStorage ?? null
  } catch {
    return null
  }
}

let storage: HandoffStorage | null = defaultStorage()

/** Test seam. Also the escape hatch if a browser's storage throws. */
export function setPlayHandoffStorage(next: HandoffStorage | null): void {
  storage = next
}

/**
 * Record which game a teacher launched, for the child's flow to pick up.
 *
 * ⚠️ NON-FATAL ON A STORAGE FAILURE, LIKE EVERY OTHER WRITE IN THIS FOLDER. The
 * session is already minted by the time this is called; a lost game choice costs
 * the child one tap on the home screen, and throwing here would cost them the
 * whole launch.
 */
export function setPlayHandoff(handoff: PlayHandoff): void {
  try {
    storage?.setItem(HANDOFF_KEY, JSON.stringify(handoff))
  } catch {
    /* See above — one tap, not a dead end. */
  }
}

/**
 * Whether a launch is waiting for this child — WITHOUT consuming it.
 *
 * 🔴 SO THE HOME SCREEN IS NEVER DRAWN ON THE WAY THROUGH. A launched child is
 * moved off `home` by an effect, i.e. after one render — and that one render of
 * the home screen requested all five game covers on school wifi, ahead of the
 * game the teacher actually picked. `KidApp` asks this first and draws nothing
 * for the instant before the effect takes the handoff.
 */
export function hasPlayHandoff(playerId: string): boolean {
  try {
    const raw = storage?.getItem(HANDOFF_KEY) ?? null
    if (!raw) return false
    const parsed: unknown = JSON.parse(raw)
    return typeof parsed === 'object' && parsed !== null && (parsed as Partial<PlayHandoff>).playerId === playerId
  } catch {
    return false
  }
}

/**
 * Read and clear the handoff for this child, if there is one.
 *
 * 🔴 READ-AND-CLEAR IN ONE CALL, so there is no path where a caller reads it and
 * forgets to consume it. A handoff that survived its own launch would re-open
 * the same game every time the child returned home.
 *
 * ⚠️ A HANDOFF FOR ANOTHER CHILD IS CLEARED RATHER THAN LEFT. It can only be
 * stale — the launch it belonged to is over, or it was for the tablet's previous
 * occupant — and leaving it would arm a surprise for whenever that child next
 * signs in on this laptop.
 */
export function takePlayHandoff(playerId: string): GameId | null {
  let raw: string | null
  try {
    raw = storage?.getItem(HANDOFF_KEY) ?? null
  } catch {
    return null
  }
  if (!raw) return null

  /* Cleared on every path below, including the malformed one: a value this
     function cannot read is a value nothing else will be able to either. */
  try {
    storage?.removeItem(HANDOFF_KEY)
  } catch {
    /* Nothing useful to do; the mismatch check below still guards the result. */
  }

  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const { playerId: forPlayer, gameId } = parsed as Partial<PlayHandoff>
    if (typeof forPlayer !== 'string' || forPlayer !== playerId) return null
    if (typeof gameId !== 'string' || gameId.length === 0) return null
    return gameId as GameId
  } catch {
    return null
  }
}

/**
 * Drop any pending handoff without consuming it as a launch.
 *
 * ⚠️ FOR THE FAILURE PATH ONLY — a launch whose session mint 403s must not leave
 * a game choice armed for the next child who happens to share the id.
 */
export function clearPlayHandoff(): void {
  try {
    storage?.removeItem(HANDOFF_KEY)
    storage?.removeItem(RETURN_KEY)
  } catch {
    /* Nothing useful to do. */
  }
}

/* ─────────────────────────────────────────── where the adult goes back to ── */

const RETURN_KEY = 'kalqy.playReturn'

/**
 * 🔴 WHERE THE **ADULT** RETURNS TO WHEN THE TABLET COMES BACK — a second,
 * deliberately separate fact from which game was launched.
 *
 * A child who signs themselves in taps Back and lands on the door picker, which
 * is right: nobody is waiting for that laptop. A teacher who launched the round
 * is standing there holding it, and sending them to a tile grid — then to a
 * sign-in form they do not need, because their own session never ended — is
 * three taps and one moment of *"have I been logged out?"* at the exact point
 * the lesson resumes.
 *
 * ⚠️ IT IS A ROUTE **ID**, TYPED AS A BARE STRING HERE ON PURPOSE. `shared/`
 * never imports from `app/` (`folderstructure.md`'s dependency direction), and
 * the caller in `app/` is the only place that can turn this back into a path.
 * The value is written by the teacher tier and read by `KidApp`; neither needs
 * this module to know what a route is.
 *
 * ⚠️ AND IT IS SEPARATE FROM THE GAME HANDOFF BECAUSE THE TWO ARE CONSUMED AT
 * DIFFERENT MOMENTS — the game at the first frame, the return at Back, which may
 * be forty minutes and six rounds later.
 */
export function setPlayReturn(routeId: string): void {
  try {
    storage?.setItem(RETURN_KEY, routeId)
  } catch {
    /* Non-fatal: the child still leaves, to the door picker. */
  }
}

/** Read and clear it. `null` for a child who signed themselves in. */
export function takePlayReturn(): string | null {
  let raw: string | null
  try {
    raw = storage?.getItem(RETURN_KEY) ?? null
    storage?.removeItem(RETURN_KEY)
  } catch {
    return null
  }
  return raw !== null && raw.length > 0 ? raw : null
}
